<template>
    <div class="sd" :class="{ 'sd--disabled': disabled }" ref="root">
        <p class="sd__title" v-if="title">{{ title }}</p>

        <!-- Single select -->
        <div
            v-if="!multiple"
            class="sd__control"
            ref="control"
            :class="{ 'sd__control--open': open, 'sd__control--disabled': disabled }"
            @click="toggle"
            :tabindex="disabled ? -1 : 0"
            @keydown.enter.prevent="toggle"
            @keydown.space.prevent="toggle"
            @keydown.escape="close"
        >
            <span class="sd__value" :class="{ 'sd__value--placeholder': !selectedLabel }">
                {{ selectedLabel || 'Select An Option' }}
            </span>
            <i class="sd__arrow fas fa-chevron-down"></i>
        </div>

        <!-- Multi select -->
        <div
            v-else
            class="sd__control sd__control--multi"
            ref="control"
            :class="{ 'sd__control--open': open, 'sd__control--disabled': disabled }"
            @click="toggle"
        >
            <div class="sd__tags" v-if="selectedTags.length">
                <span
                    class="sd__tag"
                    v-for="tag in selectedTags"
                    :key="tag.id"
                >
                    {{ tag.title }}
                    <i class="fas fa-times sd__tag-remove" @click.stop="removeTag(tag.id)"></i>
                </span>
            </div>
            <span class="sd__value sd__value--placeholder" v-else>Select Options</span>
            <i class="sd__arrow fas fa-chevron-down"></i>
        </div>

        <!-- Dropdown menu -->
        <transition name="sd-drop">
            <div class="sd__menu" v-if="open" :style="menuStyle" @mousedown.prevent>
                <div class="sd__search-wrap" v-if="options.length > 5">
                    <i class="fas fa-search sd__search-icon"></i>
                    <input
                        ref="searchInput"
                        class="sd__search"
                        type="text"
                        v-model="search"
                        placeholder="Search..."
                        @keydown.escape="close"
                        @keydown.enter.prevent="selectHighlighted"
                        @keydown.down.prevent="moveHighlight(1)"
                        @keydown.up.prevent="moveHighlight(-1)"
                    />
                </div>
                <div class="sd__options" ref="optionsList">
                    <div
                        v-if="!multiple"
                        class="sd__option sd__option--clear"
                        :class="{ 'sd__option--active': highlightIndex === -1 }"
                        @click="selectOption('')"
                        @mouseenter="highlightIndex = -1"
                    >
                        Select An Option
                    </div>
                    <div
                        class="sd__option"
                        v-for="(opt, idx) in filteredOptions"
                        :key="opt.id"
                        :class="{
                            'sd__option--selected': isSelected(opt.id),
                            'sd__option--active': highlightIndex === idx,
                        }"
                        @click="selectOption(opt.id)"
                        @mouseenter="highlightIndex = idx"
                    >
                        <span class="sd__option-check" v-if="multiple">
                            <i v-if="isSelected(opt.id)" class="fas fa-check"></i>
                        </span>
                        {{ opt.title }}
                    </div>
                    <div class="sd__no-results" v-if="!filteredOptions.length">
                        No results found
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
export default {
    emits: ["change"],
    props: {
        id: { type: String, default: "" },
        title: { type: String, default: "" },
        options: { type: Array, default: () => [] },
        multiple: { type: Boolean, default: false },
        field: { type: String, default: "" },
        value: { type: [Number, String, Object, Array], default: () => null },
        disabled: { type: Boolean, default: false },
    },
    data() {
        return {
            open: false,
            search: "",
            highlightIndex: -1,
            menuStyle: {},
        }
    },
    computed: {
        filteredOptions() {
            if (!this.search) return this.options
            const q = this.search.toLowerCase()
            return this.options.filter(
                (o) => o.title && o.title.toLowerCase().includes(q)
            )
        },
        selectedLabel() {
            if (this.multiple) return ""
            const match = this.options.find((o) => String(o.id) === String(this.value))
            return match ? match.title : ""
        },
        selectedTags() {
            if (!this.multiple || !this.value || !Array.isArray(this.value)) return []
            return this.value
                .map((v) => this.options.find((o) => String(o.id) === String(v)))
                .filter(Boolean)
        },
    },
    methods: {
        toggle() {
            if (this.disabled) return
            this.open ? this.close() : this.openMenu()
        },
        openMenu() {
            // Measure the trigger BEFORE opening so the menu panel doesn't affect the rect
            this.calcMenuPos()
            this.open = true
            this.search = ""
            this.highlightIndex = -1
            this.$nextTick(() => {
                if (this.$refs.searchInput) this.$refs.searchInput.focus()
            })
        },
        calcMenuPos() {
            // Always use the control trigger ref so the open menu never inflates the rect
            const el = this.$refs.control || this.$refs.root
            if (!el) return
            const rect = el.getBoundingClientRect()
            const spaceBelow = window.innerHeight - rect.bottom
            const openAbove = spaceBelow < 220 && rect.top > spaceBelow
            this.menuStyle = {
                position: 'fixed',
                left: `${rect.left}px`,
                width: `${Math.max(rect.width, 200)}px`,
                zIndex: 2000,
                ...(openAbove
                    ? { bottom: `${window.innerHeight - rect.top + 4}px`, top: 'auto' }
                    : { top: `${rect.bottom + 4}px`, bottom: 'auto' }
                ),
            }
        },
        close() {
            this.open = false
            this.search = ""
        },
        isSelected(id) {
            if (this.multiple) {
                return Array.isArray(this.value) && this.value.some((v) => String(v) === String(id))
            }
            return String(this.value) === String(id)
        },
        selectOption(id) {
            if (this.multiple) {
                const prev = Array.isArray(this.value) ? [...this.value] : []
                if (this.isSelected(id)) {
                    this.$emit(
                        "change",
                        prev.filter((v) => String(v) !== String(id))
                    )
                } else {
                    this.$emit("change", [...prev, id])
                }
            } else {
                this.$emit("change", id)
                this.close()
            }
        },
        removeTag(id) {
            if (this.disabled) return
            const prev = Array.isArray(this.value) ? [...this.value] : []
            this.$emit(
                "change",
                prev.filter((v) => String(v) !== String(id))
            )
        },
        selectHighlighted() {
            if (this.highlightIndex >= 0 && this.filteredOptions[this.highlightIndex]) {
                this.selectOption(this.filteredOptions[this.highlightIndex].id)
            }
        },
        moveHighlight(dir) {
            const len = this.filteredOptions.length
            if (!len) return
            this.highlightIndex = Math.max(-1, Math.min(len - 1, this.highlightIndex + dir))
            this.$nextTick(() => {
                const list = this.$refs.optionsList
                const item = list && list.children[this.highlightIndex + (this.multiple ? 0 : 1)]
                if (item) item.scrollIntoView({ block: "nearest" })
            })
        },
        onClickOutside(e) {
            if (this.$refs.root && !this.$refs.root.contains(e.target)) {
                this.close()
            }
        },
        onScrollOrResize() {
            if (this.open) this.calcMenuPos()
        },
    },
    mounted() {
        document.addEventListener("click", this.onClickOutside, true)
        window.addEventListener("scroll", this.onScrollOrResize, true)
        window.addEventListener("resize", this.onScrollOrResize)
    },
    beforeDestroy() {
        document.removeEventListener("click", this.onClickOutside, true)
        window.removeEventListener("scroll", this.onScrollOrResize, true)
        window.removeEventListener("resize", this.onScrollOrResize)
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.sd {
    position: relative;
    font-family: inherit;

    &--disabled { opacity: 0.6; pointer-events: none; }

    &__title {
        font-size: 12px;
        font-weight: 600;
        color: $gray600;
        margin: 0 0 5px;
    }

    /* ── Control ── */
    &__control {
        display: flex;
        align-items: center;
        justify-content: space-between;
        min-height: 38px;
        padding: 6px 12px;
        border: 1px solid $gray200;
        border-radius: 8px;
        background: white;
        cursor: pointer;
        transition: border-color 0.15s, box-shadow 0.15s;
        gap: 8px;

        &:hover { border-color: $gray300; }
        &--open {
            border-color: $primaryColor;
            box-shadow: 0 0 0 3px rgba($primaryColor, 0.1);
        }
        &--disabled {
            background: $gray50;
            cursor: not-allowed;
        }
        &--multi {
            flex-wrap: wrap;
            padding: 4px 10px;
        }
    }

    &__value {
        font-size: 13px;
        color: $gray800;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        flex: 1;
        min-width: 0;
        line-height: 1.4;

        &--placeholder { color: $gray400; }
    }

    &__arrow {
        font-size: 10px;
        color: $gray400;
        flex-shrink: 0;
        transition: transform 0.2s;
        .sd__control--open & { transform: rotate(180deg); }
    }

    /* ── Tags (multi) ── */
    &__tags {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        flex: 1;
        min-width: 0;
    }
    &__tag {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 2px 8px;
        border-radius: 5px;
        background: $primaryLight;
        color: $primaryColor;
        font-size: 12px;
        font-weight: 500;
        line-height: 1.5;
        max-width: 160px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    &__tag-remove {
        font-size: 9px;
        cursor: pointer;
        opacity: 0.7;
        flex-shrink: 0;
        &:hover { opacity: 1; }
    }

    /* ── Dropdown Menu ── */
    &__menu {
        /* position/top/left/width/z-index are set via inline style (fixed positioning) */
        background: white;
        border: 1px solid $gray200;
        border-radius: 10px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        overflow: hidden;
    }

    /* ── Search ── */
    &__search-wrap {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        border-bottom: 1px solid $gray100;
    }
    &__search-icon { font-size: 12px; color: $gray400; }
    &__search {
        flex: 1;
        border: none;
        outline: none;
        font-size: 13px;
        font-family: inherit;
        color: $gray700;
        background: transparent;

        &::placeholder { color: $gray400; }
    }

    /* ── Options ── */
    &__options {
        max-height: 220px;
        overflow-y: auto;
        padding: 4px;

        &::-webkit-scrollbar { width: 5px; }
        &::-webkit-scrollbar-track { background: transparent; }
        &::-webkit-scrollbar-thumb {
            background: $gray300;
            border-radius: 3px;
        }
    }
    &__option {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        font-size: 13px;
        color: $gray700;
        border-radius: 6px;
        cursor: pointer;
        transition: background 0.1s;
        line-height: 1.4;

        &:hover, &--active { background: $gray50; }
        &--selected {
            color: $primaryColor;
            font-weight: 600;
            background: $primaryLight;
        }
        &--clear {
            color: $gray400;
            font-style: italic;
        }
    }
    &__option-check {
        width: 16px;
        height: 16px;
        border-radius: 4px;
        border: 1.5px solid $gray300;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 9px;
        flex-shrink: 0;
        transition: all 0.15s;

        .sd__option--selected & {
            background: $primaryColor;
            border-color: $primaryColor;
            color: white;
        }
    }
    &__no-results {
        padding: 16px 12px;
        text-align: center;
        font-size: 13px;
        color: $gray400;
    }
}

/* ── Transition ── */
.sd-drop-enter-active { transition: opacity 0.15s, transform 0.15s; }
.sd-drop-leave-active { transition: opacity 0.1s, transform 0.1s; }
.sd-drop-enter, .sd-drop-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}
</style>
